import React, {useState, useEffect, useCallback} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Table, Tag, Tooltip} from "antd";
import {FileSearchOutlined, PhoneOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {CaretUpOutlined, CaretDownOutlined} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {ColIcon} from "./courierPageStyles";
import {formatPhoneNumber, b64DecodeToUrl} from "utils/helpers";
import {activityColors, courierCallStatusMap, courierEmploymentTypes} from "constants/constants";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {useWindowSize} from "hooks/useWindowSize";
import config from "_core/config";
import moment from "moment";
import {TableContainer} from "./ComponentStyles";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {useCourierOnboarding} from "hooks/useCourierOnboarding";
import {CreateCallRecordModal} from "./courierDetail/courierOnboardingTab/courierCallRecords/createCallRecordModal/CreateCallRecordModal";
import {useLoadingModal} from "hooks";
import {LoadingModal} from "../_defaultComponents/LoadingModal";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {normalizeCityName} from "utils/normalizeCityName";
import ActionButtons from "./newCourier/components/ActionButtons";
import CourierPageFilter from "./newCourier/components/CourierPageFilter";
import CourierEquipmentActions from "./newCourier/components/equipment/CourierEquipmentActions";
import EquipmentContractStatusTag from "./newCourier/components/equipment/EquipmentContractStatusTag";
import {
    CourierFilterContext,
    useCourierFilterProvider
} from "./newCourier/context/useCourierFilter";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";

const {Column} = Table;

export const CourierPage = () => {
    const stores = useStore();
    const windowSize = useWindowSize();
    const {t} = useTranslation([
        "courierPage",
        "courierTypes",
        "mapCourierStatuses",
        "activationSteps",
        "activationSubSteps"
    ]);
    const {renderOnboardingStatus, renderOnboardingSubStatus} = useCourierOnboarding();
    const isMobile = windowSize.width < 768;
    const filterContext = useCourierFilterProvider();
    const {state, dispatch, fetchCallStatuses, filterDependencies, isAgency} = filterContext;
    const [open, setOpen] = useState(true);
    const [fileList, setFileList] = useState([]);
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [documentCount, setDocumentCount] = useState(0);

    // Arama sonrası not girme için state'ler
    const [callNoteModalVisible, setCallNoteModalVisible] = useState(false);
    const [selectedCallRecord, setSelectedCallRecord] = useState(null);

    // Generic loading modal hook
    const {isRequestInProgress, modalVisible, requestData, executeWithModal} = useLoadingModal();

    const [loading, data, setData, setLoading] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: (state?.currentPage - 1) * 10,
            cityId: state?.cityId ? normalizeCityName(state?.cityId) : undefined,
            continent: state.continent,
            preferredCityId: state.preferredCity
                ? normalizeCityName(state.preferredCity)
                : undefined,
            preferredContinent: state.preferredContinent || undefined,
            types: state.types,
            searchText: state.searchText,
            vehicleInfo: state.vehicleInfoText,
            tckn: state.tcknText,
            activity: state.activity,
            sort: state.sort,
            activationStep: state.activationStep,
            activationSubStep: state.activationSubStep,
            trainingStatus: state.trainingStatus,
            leaveStatus: state.leaveStatus === "all" ? undefined : state.leaveStatus,
            courierCounties: state.districts || [],
            preferredDistrictIds: state.preferredDistricts || [],
            startDate:
                Array.isArray(state.selectedDateRange) &&
                state.selectedDateRange.length === 2 &&
                state.selectedDateRange[0] &&
                state.selectedDateRange[1]
                    ? moment(state.selectedDateRange[0]).format("YYYY-MM-DD")
                    : undefined,
            endDate:
                Array.isArray(state.selectedDateRange) &&
                state.selectedDateRange.length === 2 &&
                state.selectedDateRange[0] &&
                state.selectedDateRange[1]
                    ? moment(state.selectedDateRange[1]).format("YYYY-MM-DD")
                    : undefined,
            walletOwnership: state.walletOwnershipFilter || "all",
            callStatus: state.callStatus,
            contractSigningStatus: state.contractSigningStatus,
            motorTypeIds: state.motorTypeIds || [],
            drivingLicenseIds: state.drivingLicenseIds || [],
            workingModelId: state.workingModelId || [],
            hasCriminalRecord: state.hasCriminalRecord,
            is20Old: state.is20Old,
            personalCompanySetupStatus:
                state.personalCompanySetupStatus === "all"
                    ? undefined
                    : state.personalCompanySetupStatus,
            autoValidationStatus:
                state.autoValidationStatus === "all" ? undefined : state.autoValidationStatus,
            zoneManagementId: state.zoneManagementId,
            zoneIds: state.zoneId ? [state.zoneId] : [],
            employmentType: state.employmentType,
            managerCompany: state.managerCompany === "all" ? undefined : state.managerCompany
        }),
        dependencies: filterDependencies,
        initialCall: false
    });

    useEffect(() => {
        const fetchDocumentCount = async () => {
            try {
                const data = await stores.courierStore.getOnboardingDocumentCount();
                setDocumentCount(data.result);
            } catch (err) {
                console.error("Document count fetch failed:", err);
            }
        };

        fetchDocumentCount();
    }, []);

    useEffect(() => {
        fetchCallStatuses();
    }, []);

    useEffect(() => {
        if (windowSize.width < 768) {
            setOpen(false);
        }
    }, [windowSize.width, open]);

    const fileUploadProps = {
        name: "file",
        multiple: false,
        showUploadList: false,
        beforeUpload: file => {
            setFileList([...fileList, file]);
        },
        customRequest: () => {
            setLoading(true);
            const formData = new FormData();
            fileList.forEach(file => {
                formData.append("files[]", file);
            });
            fetch(`${config.getReportBasePublicUrl()}api/panel/v1/courier/import`, {
                method: "POST",
                headers: {
                    Authorization: "Basic " + stores.meStore.sessionToken
                },
                body: formData
            })
                .then(res => res.json())
                .then(async res => {
                    if (res.result.isSuccess == false) {
                        message.error(
                            t("modal.fileUpload.error", {
                                ns: "courierPage",
                                filename: fileList[0].name,
                                responseMessage: res.result.message,
                                duration: 10000
                            })
                        );
                        return;
                    } else {
                        setFileList([]);
                        const responseMessages = [];

                        if (res.result.createdCouriers.length > 0) {
                            const actionText = t("import.messages.created", {ns: "courierPage"});
                            const msg = `${res.result.createdCouriers.length} ${actionText}`;
                            responseMessages.push(<div key={msg}>{msg}</div>);
                        }
                        if (res.result.updatedCouriers.length > 0) {
                            const actionText = t("import.messages.updated", {ns: "courierPage"});
                            const msg = `${res.result.updatedCouriers.length} ${actionText}`;
                            responseMessages.push(<div key={msg}>{msg}</div>);
                        }
                        if (res.result.skippedCouriers.length > 0) {
                            const actionText = t("import.messages.skipped", {ns: "courierPage"});
                            const msg = `${res.result.skippedCouriers.length} ${actionText}`;
                            responseMessages.push(<div key={msg}>{msg}</div>);
                        }
                        if (res.result.geocodedCouriers.length > 0) {
                            const actionText = t("import.messages.geocoded", {ns: "courierPage"});
                            const msg = `${res.result.geocodedCouriers.length} ${actionText}`;
                            responseMessages.push(<div key={msg}>{msg}</div>);
                        }
                        const validationMessages = res.result.validationMessages;
                        let errorExcel = res.result.errorExcel;
                        if (errorExcel) {
                            responseMessages.push(
                                <div key={"error"}>
                                    <b>
                                        {t("modal.fileUpload.errorExcel", {
                                            ns: "courierPage"
                                        })}
                                    </b>
                                </div>
                            );
                            errorExcel = await b64DecodeToUrl(errorExcel);
                            let link = document.createElement("a");
                            link.href = errorExcel;
                            link.download = `${t("screens.couriers", {ns: "common"})}.xlsx`;
                            document.body.appendChild(link);
                            link.click();
                            setTimeout(function() {
                                window.URL.revokeObjectURL(link);
                            }, 200);
                        }

                        for (const message of validationMessages) {
                            responseMessages.push(<div key={message}>{message}</div>);
                        }
                        stores.appStore.openSuccessModal({
                            title: t("modal.fileUpload.success", {ns: "courierPage"}),
                            content: responseMessages
                        });
                    }
                })
                .catch(error => {
                    message.error(t("modal.fileUpload.uploadFailed", {ns: "courierPage"}));
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const getExcelTemplateData = async (activationLanguage = "en") => {
        setLoadingExcelData(true);
        try {
            const response = await stores.courierStore.getCourierList({
                limit: 10,
                skip: (state.currentPage - 1) * 10,
                cityId: state.cityId ? normalizeCityName(state.cityId) : undefined,
                continent: state.continent,
                preferredCityId: state.preferredCity
                    ? normalizeCityName(state.preferredCity)
                    : undefined,
                preferredContinent: state.preferredContinent || undefined,
                types: state.types,
                searchText: state.searchText,
                vehicleInfo: state.vehicleInfoText,
                tckn: state.tcknText,
                walletOwnership: state.walletOwnershipFilter,
                activity: state.activity,
                sort: state.sort,
                activationStep: state.activationStep,
                activationSubStep: state.activationSubStep,
                trainingStatus: state.trainingStatus,
                leaveStatus: state.leaveStatus === "all" ? undefined : state.leaveStatus,
                courierCounties: state.districts || [],
                preferredDistricts: state.preferredDistricts || [],
                startDate:
                    Array.isArray(state.selectedDateRange) && state.selectedDateRange.length == 2
                        ? moment(state.selectedDateRange[0]).format("YYYY-MM-DD")
                        : undefined,
                endDate:
                    Array.isArray(state.selectedDateRange) && state.selectedDateRange.length == 2
                        ? moment(state.selectedDateRange?.[1]).format("YYYY-MM-DD")
                        : undefined,
                excel: true,
                activationLanguage,
                callStatus: state.callStatus,
                motorTypeIds: state.motorTypeIds || [],
                drivingLicenseIds: state.drivingLicenseIds || [],
                workingModelId: state.workingModelId || [],
                employmentType: state.employmentType,
                managerCompany: state.managerCompany === "all" ? undefined : state.managerCompany
            });
            const base64Data = response?.base64Excel;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "courierList.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoadingExcelData(false);
        }
    };

    const toggleSortField = field => {
        const current = state.sort.find(s => s.startsWith(`${field}_`));

        let newSort;
        if (!current) {
            newSort = [`${field}_asc`];
        } else {
            const direction = current.split("_").pop();
            newSort = [direction === "asc" ? `${field}_desc` : `${field}_asc`];
        }

        dispatch({
            type: "sort",
            payload: newSort
        });
    };

    const getSortIcon = field => {
        const sortField = state.sort.find(s => s.startsWith(`${field}_`));
        if (sortField?.includes("asc")) {
            return <CaretUpOutlined />;
        }
        if (sortField?.includes("desc")) {
            return <CaretDownOutlined />;
        }
        return <span style={{marginLeft: 5, opacity: 0.3}}>⇅</span>;
    };

    const renderDetailCell = text => (
        <CustomButton
            href={"/couriers/" + text}
            data-testid="courierDetailButton"
            onClick={event => {
                event.preventDefault();
                window.open("/couriers/" + text, "_blank");
            }}
            icon={<FileSearchOutlined />}
        />
    );

    const renderApplicationDateTitle = () => (
        <button
            type="button"
            style={{
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                display: "flex",
                alignItems: "left"
            }}
            onClick={() => toggleSortField("applicationDate")}
        >
            Başvuru Tarihi
            {getSortIcon("applicationDate")}
        </button>
    );

    const renderApplicationDateCell = (applicationDate, record) => (
        <span>
            {applicationDate ? (
                <>
                    {moment(applicationDate).format("DD.MM.YYYY")} <br />
                    {moment(applicationDate).format("HH:mm")}
                </>
            ) : (
                "-"
            )}
            <br />

            {record?.reApplyCount ? <Tag>Başvuru Sayısı: {record?.reApplyCount}</Tag> : null}
        </span>
    );

    const renderPreferredCityCell = (_, courier) => (
        <Tag data-testid="preferredCity">
            {courier?.preferredCity?.name?.toLocaleLowerCase("TR-tr")?.capitalize()}
        </Tag>
    );

    const renderPreferredDistrictsCell = (_, courier) => (
        <Tooltip
            title={courier?.preferredDistricts
                ?.map(district => district?.name?.toLocaleLowerCase("TR-tr")?.capitalize())
                .join(", ")}
        >
            <Tag data-testid="preferredDistricts">
                {courier?.preferredDistricts?.length > 1
                    ? courier?.preferredDistricts[0]?.name
                          ?.toLocaleLowerCase("TR-tr")
                          ?.capitalize() + "..."
                    : courier?.preferredDistricts?.[0]?.name
                          ?.toLocaleLowerCase("TR-tr")
                          ?.capitalize()}
            </Tag>
        </Tooltip>
    );

    const renderPreferredCityDistrictCell = (_, courier) => (
        <>
            {renderPreferredCityCell(_, courier)}
            <br />
            {renderPreferredDistrictsCell(_, courier)}
        </>
    );

    const renderPhoneCell = (text, record) => {
        return (
            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                <span data-testid="courierPhone">{formatPhoneNumber(text)}</span>
                {stores.meStore.canCallCouriers && (
                    <CustomButton
                        size="small"
                        type="primary"
                        icon={<PhoneOutlined />}
                        disabled={isRequestInProgress}
                        onClick={async () => {
                            // prevent double click
                            if (isRequestInProgress) {
                                return;
                            }

                            try {
                                const result = await executeWithModal(
                                    () =>
                                        stores.callStore.click2Call({
                                            courierId: record._id,
                                            phoneNumber: text,
                                            priority: "normal"
                                        }),
                                    {
                                        title: "Arama İsteği Gönderildi",
                                        message: "Arama isteği gönderiliyor...",
                                        subMessage:
                                            "Lütfen bekleyin, çağrınız kısa sürede başlatılacaktır.",
                                        icon: PhoneOutlined,
                                        iconColor: "#1890ff"
                                    }
                                );

                                if (result.success && result.data?.callRecordId) {
                                    setSelectedCallRecord({
                                        callRecordId: result.data.callRecordId,
                                        courierId: record._id,
                                        phoneNumber: text
                                    });
                                    setCallNoteModalVisible(true);
                                }
                            } catch (error) {
                                stores.appStore.openErrorModal({
                                    content:
                                        "Arama başlatılırken beklenmeyen bir hata oluştu. Lütfen tekrar deneyin."
                                });
                            }
                        }}
                        title="AloTech ile Arama Yap"
                        style={{
                            minWidth: "60px",
                            padding: "4px 12px",
                            height: "28px",
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            color: "white",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            borderRadius: "16px"
                        }}
                    >
                        Ara
                    </CustomButton>
                )}
            </div>
        );
    };

    // VGT-329: Acente listesinde aday kırmızı, aktif yeşil gösterilir.
    const agencyActivationStepColors = {
        candidate: "red",
        approved: "green"
    };

    const renderAgencyActivationStep = activationStep =>
        activationStep === "approved" ? "Aktif" : renderOnboardingStatus({activationStep});

    const getDataTitles = () => {
        if (isAgency) {
            return [
                t("actions.detail", {ns: "common"}),
                t("fields.courierStatus", {ns: "common"}),
                "Başvuru Tarihi",
                "Çalışmak İstenen İl/İlçe",
                t("courierPageFields.fullname", {ns: "courierPage"}),
                t("courierPageFields.tckn", {ns: "courierPage"}),
                t("courierPageFields.cellphone", {ns: "courierPage"}),
                "Ekipman Sözleşme Durumu",
                "Ekipman"
            ];
        }

        const baseTitles = [
            t("actions.detail", {ns: "common"}),
            t("fields.activationStep", {ns: "common"}),
            t("fields.courierStatus", {ns: "common"}),
            "Başvuru Tarihi",
            "Çalışmak İstenen İl/İlçe",
            t("courierPageFields.fullname", {ns: "courierPage"}),
            t("courierPageFields.tckn", {ns: "courierPage"}),
            t("courierPageFields.cellphone", {ns: "courierPage"}),
            "Son Arama Durumu"
        ];

        let dataTitles = [...baseTitles];

        if (state.activationSubStep === "shiftWaiting") {
            const fullnameLabel = t("courierPageFields.fullname", {ns: "courierPage"});
            const fullnameIndex = dataTitles.indexOf(fullnameLabel);

            if (fullnameIndex !== -1) {
                dataTitles.splice(fullnameIndex, 0, "Proje");
            }

            dataTitles = dataTitles.filter(title => title !== "Son Arama Durumu");
        }

        return dataTitles;
    };

    return useObserver(() => (
        <CourierFilterContext.Provider value={filterContext}>
            <VFlex vertical gap={16} data-testid="courierScreen">
                <CourierPageFilter />
                <VFlex gap={16} justify="end">
                    <ActionButtons
                        loadingExcelData={loadingExcelData}
                        getExcelTemplateData={getExcelTemplateData}
                        setLoadingExcelData={setLoadingExcelData}
                        fileUploadProps={fileUploadProps}
                        dataCount={data?.dataCount || 0}
                    />
                </VFlex>
                <TableContainer
                    size="small"
                    small={open ? "medium" : "small"}
                    loading={loading}
                    dataSource={data.rows}
                    rowKey="_id"
                    data-testid="table"
                    scroll={{x: "max-content"}}
                    showExpandColumn
                    pagination={{
                        total: data.dataCount,
                        current: state.currentPage,
                        showSizeChanger: false,
                        onChange: page => {
                            dispatch({type: "currentPage", payload: page});
                        }
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                    display={data?.rows?.length > 0 ? "grid" : "flex"}
                    dataTitles={getDataTitles()}
                    rowClassName={record => {
                        if (
                            state.activationSubStep === "documentsInReview" &&
                            record?.metaData?.documentRevisionInfo?.revisionCount > 0
                        ) {
                            return "courier-revision-row";
                        }
                        return "";
                    }}
                >
                    {isAgency ? (
                        <>
                            <Column
                                title={t("actions.detail", {ns: "common"})}
                                dataIndex="_id"
                                render={renderDetailCell}
                            />
                            <Column
                                title={
                                    <>
                                        {t("fields.courierStatus", {ns: "common"})}
                                        <br />
                                        <span
                                            style={{
                                                borderTop: "1px solid #00000029",
                                                paddingTop: "2px"
                                            }}
                                        >
                                            İşe Alım Durumu
                                        </span>
                                    </>
                                }
                                dataIndex="activationStep"
                                render={(activationStepValue, courier) => (
                                    <>
                                        <Tag
                                            color={agencyActivationStepColors[activationStepValue]}
                                            data-testid="activationStep"
                                        >
                                            {renderAgencyActivationStep(activationStepValue)}
                                        </Tag>
                                        <br />
                                        <Tag data-testid="activationSubStep">
                                            {renderOnboardingSubStatus({
                                                activationSubStep: courier?.activationSubStep,
                                                activationStep: courier?.activationStep
                                            })}
                                        </Tag>
                                    </>
                                )}
                            />
                            <Column
                                title={renderApplicationDateTitle()}
                                dataIndex="applicationDate"
                                align="left"
                                render={renderApplicationDateCell}
                            />
                            <Column
                                title={
                                    <>
                                        Çalışmak İstenen <br />
                                        Şehir - İlçe(ler)
                                    </>
                                }
                                dataIndex="preferredCity"
                                render={renderPreferredCityDistrictCell}
                            />
                            <Column
                                title={t("courierPageFields.fullname", {ns: "courierPage"})}
                                dataIndex="fullname"
                                render={text => <span data-testid="courierName">{text}</span>}
                            />
                            <Column
                                title={t("courierPageFields.tckn", {ns: "courierPage"})}
                                dataIndex="tckn"
                                width={100}
                                render={text => <span data-testid="courierTckn">{text}</span>}
                            />
                            <Column
                                title={t("courierPageFields.cellphone", {ns: "courierPage"})}
                                dataIndex="cellPhone"
                                render={renderPhoneCell}
                            />
                            <Column
                                title="Ekipman Sözleşme Durumu"
                                dataIndex={["equipmentInfo", "contractStatus"]}
                                render={(_, courier) => (
                                    <span data-testid="equipmentContractStatus">
                                        <EquipmentContractStatusTag courier={courier} />
                                    </span>
                                )}
                            />
                            <Column
                                title="Ekipman"
                                align="right"
                                render={(_, courier) =>
                                    stores.meStore.checkPermission("couriers.equipment") && (
                                        <CourierEquipmentActions
                                            courier={courier}
                                            onComplete={() =>
                                                dispatch({type: "recall", payload: !state.recall})
                                            }
                                        />
                                    )
                                }
                            />
                        </>
                    ) : (
                        <>
                            <Column
                                title={t("actions.detail", {ns: "common"})}
                                dataIndex="_id"
                                render={renderDetailCell}
                            />
                            <Column
                                title={
                                    <>
                                        {t("fields.activationStep", {ns: "common"})}
                                        <br />
                                        <span
                                            style={{
                                                borderTop: "1px solid #00000029",
                                                paddingTop: "2px"
                                            }}
                                        >
                                            {" "}
                                            İşe Alım Durumu
                                        </span>
                                    </>
                                }
                                dataIndex="activationStep"
                                render={(activationStepValue, courier) => {
                                    const uploadedDocumentCount =
                                        Object.values(courier?.documents || {})?.filter(
                                            doc => !!doc?.publicUrl && doc?.status !== "rejected"
                                        )?.length || 0;

                                    return (
                                        <>
                                            <Tag data-testid="activationStep">
                                                {renderOnboardingStatus({
                                                    activationStep: activationStepValue
                                                })}
                                            </Tag>
                                            <br />
                                            <Tag data-testid="activationSubStep">
                                                {renderOnboardingSubStatus({
                                                    activationSubStep: courier?.activationSubStep,
                                                    activationStep: courier?.activationStep
                                                })}
                                            </Tag>
                                            <br />
                                            {[
                                                "documentsInReview",
                                                "documentsUpload",
                                                "documentsRejected"
                                            ].includes(courier?.activationSubStep) && (
                                                <Tag>{`${uploadedDocumentCount}/${documentCount}`}</Tag>
                                            )}
                                        </>
                                    );
                                }}
                            />
                            <ColIcon
                                title={t("fields.courierActivity", {ns: "common"})}
                                width={50}
                                dataIndex="disabled"
                                render={disabled => (
                                    <>
                                        {disabled ? (
                                            <span style={{color: activityColors[!disabled]}}>
                                                {t("courierActivityStatuses.inactiveCouriers", {
                                                    ns: "courierPage"
                                                })}
                                            </span>
                                        ) : (
                                            <span style={{color: activityColors[!disabled]}}>
                                                {t("courierActivityStatuses.activeCouriers", {
                                                    ns: "courierPage"
                                                })}
                                            </span>
                                        )}
                                    </>
                                )}
                            />
                            <Column
                                title={renderApplicationDateTitle()}
                                dataIndex="applicationDate"
                                align="left"
                                render={renderApplicationDateCell}
                            />
                            <Column
                                title={
                                    <button
                                        type="button"
                                        style={{
                                            cursor: "pointer",
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            font: "inherit",
                                            color: "inherit",
                                            display: "flex",
                                            alignItems: "left"
                                        }}
                                        onClick={() =>
                                            toggleSortField("lastOnboardingStatusUpdatedAt")
                                        }
                                    >
                                        Son Durum Güncelleme Tarihi
                                        {getSortIcon("lastOnboardingStatusUpdatedAt")}
                                    </button>
                                }
                                dataIndex="lastOnboardingStatusUpdatedAt"
                                align="left"
                                render={date => (
                                    <span>
                                        {date ? (
                                            <>
                                                {moment(date).format("DD.MM.YYYY")}
                                                <br />
                                                {moment(date).format("HH:mm")}
                                            </>
                                        ) : (
                                            "-"
                                        )}
                                    </span>
                                )}
                            />
                            <Column
                                title={
                                    <>
                                        Çalışmak İstenen <br />
                                        Şehir - İlçe(ler)
                                    </>
                                }
                                dataIndex="preferredCity"
                                render={(_, courier) => (
                                    <>
                                        <Tag data-testid="preferredCity">
                                            {courier?.preferredCity?.name
                                                ?.toLocaleLowerCase("TR-tr")
                                                ?.capitalize()}
                                        </Tag>
                                        <br />
                                        <Tooltip
                                            title={courier?.preferredDistricts
                                                ?.map(district =>
                                                    district?.name
                                                        ?.toLocaleLowerCase("TR-tr")
                                                        ?.capitalize()
                                                )
                                                .join(", ")}
                                        >
                                            <Tag data-testid="preferredDistricts">
                                                {courier?.preferredDistricts.length > 1
                                                    ? courier?.preferredDistricts[0]?.name
                                                          ?.toLocaleLowerCase("TR-tr")
                                                          ?.capitalize() + "..."
                                                    : courier?.preferredDistricts[0]?.name
                                                          ?.toLocaleLowerCase("TR-tr")
                                                          ?.capitalize()}
                                            </Tag>
                                        </Tooltip>
                                    </>
                                )}
                            />
                            <Column
                                title="Çalışma Şekli"
                                dataIndex="employmentType"
                                render={(employmentType, record) => (
                                    <span data-testid="employmentType">
                                        {courierEmploymentTypes.find(
                                            type => type.value === employmentType
                                        )?.name || "-"}
                                    </span>
                                )}
                            />
                            <Column
                                title="Proje Bilgisi"
                                dataIndex="projectInfo"
                                render={(projectInfo, record) => (
                                    <span data-testid="projectInfo">{projectInfo || "-"}</span>
                                )}
                            />
                            {state.activationSubStep === "shiftWaiting" ? (
                                <Column
                                    title={<>Proje</>}
                                    dataIndex="dedicatedCompany"
                                    render={(_, courier) => (
                                        <>
                                            <Tag data-testid="dedicatedCompany">
                                                {courier?.dedicatedCompany?.companyName}
                                            </Tag>
                                            <br />
                                            <Tooltip
                                                title={courier?.dedicatedSubCompanies
                                                    ?.map(subCompany => subCompany?.name)
                                                    .join(", ")}
                                            >
                                                <Tag data-testid="dedicatedSubCompanies">
                                                    {courier?.dedicatedSubCompanies?.length > 1
                                                        ? courier?.dedicatedSubCompanies[0]?.name +
                                                          "..."
                                                        : courier?.dedicatedSubCompanies[0]?.name}
                                                </Tag>
                                            </Tooltip>
                                        </>
                                    )}
                                />
                            ) : (
                                <></>
                            )}
                            <Column
                                title={t("courierPageFields.fullname", {ns: "courierPage"})}
                                dataIndex="fullname"
                                render={text => <span data-testid="courierName">{text}</span>}
                            />
                            <Column
                                title={t("courierPageFields.tckn", {ns: "courierPage"})}
                                dataIndex="tckn"
                                width={100}
                                render={text => <span data-testid="courierTckn">{text}</span>}
                            />
                            <Column
                                title={t("courierPageFields.cellphone", {ns: "courierPage"})}
                                dataIndex="cellPhone"
                                render={renderPhoneCell}
                            />

                            {state.activationSubStep === "documentsInReview" && (
                                <>
                                    <Column
                                        title={
                                            <>
                                                Revizyon Sayısı
                                                <br />
                                                <span
                                                    style={{
                                                        borderTop: "1px solid #00000029",
                                                        paddingTop: "2px"
                                                    }}
                                                >
                                                    Revizyon Nedeni
                                                </span>
                                            </>
                                        }
                                        dataIndex={[
                                            "metaData",
                                            "documentRevisionInfo",
                                            "revisionCount"
                                        ]}
                                        render={(count, record) => (
                                            <>
                                                <Tag>{count || 0}</Tag>
                                                <br />
                                                <Tag>
                                                    <Shortener
                                                        text={
                                                            record?.metaData?.documentRevisionInfo
                                                                ?.lastRevisionReason
                                                        }
                                                    />
                                                </Tag>
                                            </>
                                        )}
                                    />
                                </>
                            )}
                            {state.activationSubStep === "shiftWaiting" ? (
                                <></>
                            ) : (
                                <Column
                                    title={
                                        <>
                                            Son Arama Tarihi
                                            <br />
                                            <span
                                                style={{
                                                    borderTop: "1px solid #00000029",
                                                    paddingTop: "2px"
                                                }}
                                            >
                                                Son Arama Durumu
                                            </span>
                                        </>
                                    }
                                    dataIndex={["callRecord", "createdAt"]}
                                    render={(createdAt, row) => (
                                        <Row style={{flexDirection: "column"}}>
                                            <span>
                                                {row.lastCallRecord?.createdAt
                                                    ? moment(row.lastCallRecord.createdAt).format(
                                                          "DD.MM.YYYY HH:mm"
                                                      )
                                                    : "-"}
                                            </span>

                                            <span>
                                                {courierCallStatusMap[
                                                    row.lastCallRecord?.callStatus
                                                ] ||
                                                    row.lastCallRecord?.callStatus ||
                                                    courierCallStatusMap.notCalled}
                                            </span>
                                            <span>
                                                {stores.meStore.checkRole(
                                                    "updateCourierCallRecords"
                                                ) && (
                                                    <CreateCallRecordModal
                                                        courierId={row?._id?.toString()}
                                                        onComplete={() => {
                                                            dispatch({
                                                                type: "recall",
                                                                payload: !state.recall
                                                            });
                                                        }}
                                                        buttonSize="small"
                                                        buttonType="default"
                                                    />
                                                )}
                                            </span>
                                        </Row>
                                    )}
                                />
                            )}
                        </>
                    )}
                </TableContainer>

                {/* Generic Loading Modal */}
                <LoadingModal
                    visible={modalVisible}
                    title={requestData?.title}
                    message={requestData?.message}
                    subMessage={requestData?.subMessage}
                    icon={requestData?.icon}
                    iconColor={requestData?.iconColor}
                    showSpinner={requestData?.showSpinner}
                    spinnerText={requestData?.spinnerText}
                />

                {/* Arama sonrası not girme modalı */}
                <CreateCallRecordModal
                    isCallNoteMode={true}
                    visible={callNoteModalVisible}
                    onClose={() => {
                        setCallNoteModalVisible(false);
                        setSelectedCallRecord(null);
                    }}
                    callRecordId={selectedCallRecord?.callRecordId}
                    courierId={selectedCallRecord?.courierId}
                    phoneNumber={selectedCallRecord?.phoneNumber}
                    onComplete={() => {
                        dispatch({type: "recall", payload: !state.recall});
                    }}
                />
            </VFlex>
        </CourierFilterContext.Provider>
    ));
};
